"use client"

import { Dispatch, SetStateAction, useEffect, useRef, useState, useCallback } from "react"
import { HugeiconsIcon } from "@hugeicons/react"
import {
  ArrowLeft01Icon,
  ArrowRight01Icon,
  Cancel01Icon,
} from "@hugeicons/core-free-icons"
import { Typography } from "../ui/typography"
import Image from "next/image"
import { Button } from "../ui/button"
import { ResponsiveModal } from "../ui/responsive-modal"
import { cn } from "@/lib/utils"
import { StoryItem } from "@/types/story"
import { useStoryItems } from "@/lib/react-query/queries/contents/use-story-items"
import { Skeleton } from "../ui/skeleton"
import { SessionManager } from "@/lib/session/SessionManager"
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselProgressBars,
  useCarousel,
  type CarouselApi,
} from "@/components/ui/carousel"

interface StorySectionSlideProps {
  openGroupId: string | null
  setOpenGroupId: Dispatch<SetStateAction<string | null>>
}

// Story image component with loading state
function StoryImage({
  item,
  isPriority,
  onTouchStart,
  onTouchEnd,
}: {
  item: StoryItem
  isPriority: boolean
  onTouchStart: () => void
  onTouchEnd: () => void
}) {
  const [isLoading, setIsLoading] = useState(true)

  return (
    <div
      className="relative w-full h-full"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      onMouseDown={onTouchStart}
      onMouseUp={onTouchEnd}
    >
      {isLoading && (
        <Skeleton className="absolute inset-0 w-full h-full md:rounded-[16px]" />
      )}
      <Image
        src={item.image.fileKey}
        alt={item.image.altText ?? item.title ?? "Story image"}
        width={800}
        height={400}
        className="object-cover size-full md:rounded-[16px]"
        priority={isPriority}
        onLoad={() => setIsLoading(false)}
      />
    </div>
  )
}

// Inner content component that uses carousel context
function StoryCarouselContent({
  storyItems,
  currentSlide,
  progress,
  onTouchStart,
  onTouchEnd,
  onSlideChange,
}: {
  storyItems: StoryItem[]
  currentSlide: number
  progress: number
  onTouchStart: () => void
  onTouchEnd: () => void
  onSlideChange: (index: number) => void
}) {
  const { api } = useCarousel()

  useEffect(() => {
    if (!api) return

    const handleSelect = () => {
      const newIndex = api.selectedScrollSnap()
      onSlideChange(newIndex)
    }

    api.on("select", handleSelect)
    return () => {
      api.off("select", handleSelect)
    }
  }, [api, onSlideChange])

  // Scroll to specific slide when currentSlide changes externally
  useEffect(() => {
    if (!api) return
    if (api.selectedScrollSnap() !== currentSlide) {
      api.scrollTo(currentSlide)
    }
  }, [api, currentSlide])

  return (
    <>
      <div className="absolute top-0 left-0 right-0 z-20">
        <CarouselProgressBars progress={progress} />
      </div>
      <CarouselContent className="h-full">
        {storyItems.map((item: StoryItem, index: number) => (
          <CarouselItem key={item.id} className="h-full pl-0">
            <StoryImage
              item={item}
              isPriority={index === 0}
              onTouchStart={onTouchStart}
              onTouchEnd={onTouchEnd}
            />
          </CarouselItem>
        ))}
      </CarouselContent>
    </>
  )
}

const StorySectionSlide = ({
  openGroupId,
  setOpenGroupId,
}: StorySectionSlideProps) => {
  const {
    data: storyResponse,
    isLoading,
    isError,
    error,
  } = useStoryItems(openGroupId)

  const storyItems = storyResponse?.data || []

  // Timer state
  const [currentSlide, setCurrentSlide] = useState(0)
  const [isPlaying, setIsPlaying] = useState(false)
  const [progress, setProgress] = useState(0)
  const [api, setApi] = useState<CarouselApi>()
  const intervalRef = useRef<NodeJS.Timeout | null>(null)
  const startTimeRef = useRef<number>(0)
  const pausedTimeRef = useRef<number>(0)

  // Update session activity when stories are interacted with
  useEffect(() => {
    if (openGroupId && !isLoading) {
      SessionManager.updateSessionActivity()
    }
  }, [openGroupId, isLoading])

  // Track slide changes
  useEffect(() => {
    if (openGroupId && storyItems.length > 0) {
      SessionManager.updateSessionActivity()
      console.log(
        `📖 Story slide changed: ${currentSlide + 1}/${storyItems.length}`
      )
    }
  }, [currentSlide, openGroupId, storyItems.length])

  const clearTimer = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
  }, [])

  const startTimer = useCallback(() => {
    pausedTimeRef.current = 0
    setProgress(0)
  }, [])

  // Start timer when stories are loaded and modal opens
  useEffect(() => {
    if (storyItems.length > 0 && openGroupId && !isLoading) {
      setCurrentSlide(0)
      setProgress(0)
      setIsPlaying(true)
      startTimer()
    } else {
      setIsPlaying(false)
      clearTimer()
    }

    return () => clearTimer()
  }, [storyItems.length, openGroupId, isLoading, clearTimer, startTimer])

  const handleClose = useCallback(() => {
    clearTimer()
    setOpenGroupId(null)
    setCurrentSlide(0)
    setProgress(0)
    setIsPlaying(false)
    pausedTimeRef.current = 0
    console.log("📖 Story closed")
  }, [clearTimer, setOpenGroupId])

  const handleStoryComplete = useCallback(() => {
    clearTimer()

    if (currentSlide < storyItems.length - 1) {
      const nextSlide = currentSlide + 1
      setCurrentSlide(nextSlide)
      setProgress(0)
      pausedTimeRef.current = 0
      api?.scrollTo(nextSlide)
      setIsPlaying(true)
    } else {
      handleClose()
    }
  }, [api, clearTimer, currentSlide, handleClose, storyItems.length])

  // Timer effect for smooth progress animation
  useEffect(() => {
    if (isPlaying && storyItems.length > 0) {
      const currentStory = storyItems[currentSlide]
      if (!currentStory) return

      const duration = currentStory.showDurationSeconds * 1000

      startTimeRef.current = Date.now() - pausedTimeRef.current

      intervalRef.current = setInterval(() => {
        const elapsed = Date.now() - startTimeRef.current
        const newProgress = Math.min((elapsed / duration) * 100, 100)

        setProgress(newProgress)

        if (newProgress >= 100) {
          handleStoryComplete()
        }
      }, 16)
    } else {
      clearTimer()
    }

    return () => clearTimer()
  }, [isPlaying, currentSlide, storyItems, clearTimer, handleStoryComplete])

  const pauseTimer = useCallback(() => {
    if (intervalRef.current) {
      const currentStory = storyItems[currentSlide]
      if (currentStory) {
        const duration = currentStory.showDurationSeconds * 1000
        pausedTimeRef.current = (progress / 100) * duration
      }
    }
    setIsPlaying(false)
  }, [currentSlide, progress, storyItems])

  const resumeTimer = useCallback(() => {
    setIsPlaying(true)
  }, [])

  const handleSlideChange = useCallback((newSlide: number) => {
    if (newSlide !== currentSlide) {
      setCurrentSlide(newSlide)
      setProgress(0)
      pausedTimeRef.current = 0
      setIsPlaying(true)
    }
  }, [currentSlide])

  const handlePrevSlide = useCallback(() => {
    if (currentSlide > 0) {
      const prevSlide = currentSlide - 1
      setCurrentSlide(prevSlide)
      setProgress(0)
      pausedTimeRef.current = 0
      api?.scrollTo(prevSlide)
      setIsPlaying(true)
    }
  }, [api, currentSlide])

  const handleNextSlide = useCallback(() => {
    if (currentSlide < storyItems.length - 1) {
      const nextSlide = currentSlide + 1
      setCurrentSlide(nextSlide)
      setProgress(0)
      pausedTimeRef.current = 0
      api?.scrollTo(nextSlide)
      setIsPlaying(true)
    } else {
      handleClose()
    }
  }, [api, currentSlide, handleClose, storyItems.length])

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="w-full h-dvh md:h-[656px] md:w-[360px] flex items-center justify-center">
          <Skeleton className="w-full h-full md:h-[656px] md:w-[360px] md:rounded-[16px]" />
        </div>
      )
    }

    if (isError) {
      return (
        <div className="w-full h-dvh md:h-[656px] md:w-[360px] flex items-center justify-center">
          <div className="text-center text-white">
            <Typography className="mb-2">خطا در لود استوری ها</Typography>
            <Typography variant="captionRegular" className="text-gray-300">
              {error?.message || "Something went wrong"}
            </Typography>
          </div>
        </div>
      )
    }

    if (storyItems.length === 0) {
      return (
        <div className="w-full h-dvh md:h-[656px] md:w-[360px] flex items-center justify-center">
          <Typography className="text-white">استوری یافت نشد</Typography>
        </div>
      )
    }

    return (
      <div className="relative w-full h-dvh md:h-[656px] md:w-[360px]">
        <Carousel
          opts={{
            loop: false,
            watchDrag: true,
          }}
          setApi={setApi}
          dir="ltr" // Stories usually navigate LTR even in RTL apps
          className={cn("w-full h-full !mx-0 relative")}
        >
          <StoryCarouselContent
            storyItems={storyItems}
            currentSlide={currentSlide}
            progress={progress}
            onTouchStart={pauseTimer}
            onTouchEnd={resumeTimer}
            onSlideChange={handleSlideChange}
          />
        </Carousel>
      </div>
    )
  }

  return (
    <ResponsiveModal
      isOpen={!!openGroupId}
      onClose={handleClose}
      className="bg-transparent border-transparent shadow-none [&>button]:hidden"
    >
      <Button
        onClick={handleClose}
        variant={"ghost"}
        className="absolute top-1 md:top-4 right-2 md:right-4 h-auto z-30 md:static !flex hover:bg-transparent rounded-lg py-2 px-3 focus:outline-none items-center gap-2 text-white self-start ml-auto cursor-pointer"
      >
        <HugeiconsIcon
          className="size-[26px]"
          icon={Cancel01Icon}
          strokeWidth={3}
        />
        <Typography className="text-[18px] font-normal hidden md:block">
          بستن
        </Typography>
      </Button>

      <div className="flex items-center justify-center gap-2 relative">
        {!isLoading && !isError && storyItems.length > 1 && (
          <>
            <Button
              onClick={handlePrevSlide}
              disabled={currentSlide === 0}
              className="absolute top-1/2 right-0 -translate-y-1/2 z-10 md:static md:translate-y-0 h-auto hover:bg-transparent text-white disabled:invisible disabled:pointer-events-none"
              variant="ghost"
            >
              <HugeiconsIcon icon={ArrowRight01Icon} className="size-14" />
            </Button>
            {renderContent()}

            <Button
              onClick={handleNextSlide}
              className="absolute top-1/2 left-0 -translate-y-1/2 z-10 md:static md:translate-y-0 h-auto hover:bg-transparent text-white disabled:invisible disabled:pointer-events-none"
              variant="ghost"
            >
              <HugeiconsIcon icon={ArrowLeft01Icon} className="size-14" />
            </Button>
          </>
        )}
        {!isLoading && !isError && storyItems.length === 1 && renderContent()}
        {(isLoading || isError || storyItems.length === 0) && renderContent()}
      </div>
    </ResponsiveModal>
  )
}

export default StorySectionSlide
